import { useAuth } from '@/hooks/use-auth'
import { getUserDisplayName } from '@/types'
import { ChevronDown, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Link, Outlet, useNavigate } from 'react-router-dom'

interface SupportStandaloneLayoutProps {
  /** Where the header logo links -- defaults to Benefits' own hub.
   * Parameterized so this shared layout can be reused by another product
   * surface (e.g. Exit Guide's detail page) without sending its users back
   * to the wrong hub. */
  logoHref?: string
}

export function SupportStandaloneLayout({ logoHref = '/benefits' }: SupportStandaloneLayoutProps) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [logoLoadFailed, setLogoLoadFailed] = useState(false)

  const displayName = getUserDisplayName(user)
  const displayInitial = displayName[0]?.toUpperCase() || 'U'

  useEffect(() => {
    if (!showUserMenu) return
    const handleClickOutside = (e: globalThis.MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('[data-user-menu]')) {
        setShowUserMenu(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [showUserMenu])

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-canvas">
      <header className="h-14 shrink-0 flex items-center justify-between gap-4 px-5">
        <Link to={logoHref} className="flex items-center shrink-0">
          <div className="h-5 w-auto max-w-[100px] overflow-hidden bg-transparent shrink-0">
            {!logoLoadFailed ? (
              <img
                src="/logo-black.png"
                alt="Pelgo"
                className="h-full w-full object-contain object-left"
                onError={() => setLogoLoadFailed(true)}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-sm font-bold text-foreground">
                P
              </div>
            )}
          </div>
        </Link>

        <div className="flex items-center gap-3 shrink-0">
          <div data-user-menu className="relative shrink-0">
            <button
              type="button"
              onClick={() => setShowUserMenu((prev) => !prev)}
              className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 transition-colors hover:bg-muted"
            >
              <div className="w-8 h-8 rounded-full border flex items-center justify-center text-xs font-medium shrink-0 bg-muted border-border text-muted-foreground">
                {displayInitial}
              </div>
              <span className="max-w-[140px] truncate text-sm font-medium text-muted-foreground">
                {displayName}
              </span>
              <ChevronDown
                className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {showUserMenu && (
              <div className="absolute top-full right-0 mt-1 min-w-[180px] rounded-lg border bg-background shadow-lg py-1 z-50">
                <button
                  type="button"
                  onClick={async () => {
                    setShowUserMenu(false)
                    try {
                      await logout()
                      navigate('/login', { replace: true })
                    } catch (error) {
                      console.error('Logout failed:', error)
                    }
                  }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-sm text-danger-600 hover:bg-muted transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  Log out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  )
}
